import { useTranslation } from '../../../lib/i18n';
import { MAESTRO_PILE_ID } from '../../../lib/agent-pile';
import { PageLoader, Typography } from '../ui';
import { AgentsEmptyIntro } from './AgentsEmptyIntro';
import { DestinationCard, KnowledgeIcon } from './DestinationCard';
import { AgentPile } from './AgentPile';
import { TeamGenomeCard } from './TeamGenomeCard';
import { FilesSummaryCard } from './FilesSummaryCard';
import { AppsSummaryCard } from './AppsSummaryCard';
import { TasksSummary } from '../tasks/TasksSummary';
import { RoutinesSummary } from '../routines/RoutinesSummary';
import { useSummaryTilesReady } from './use-summary-tiles-ready';
import { useYourAgents } from './use-your-agents';
import styles from './YourAgentsDesktop.module.css';

// Your Agents, wide (Figma `10261:86167`): a three-up top row — the routines summary, the tasks
// summary and the Files tile — then the genome next to the agent card pile, and the "what they know
// about you" destination along the foot.
//
// The Files tile lives at the TOP here, sharing a row with the tasks summary at 498px each
// (24px gutter), which is where this frame puts it — and it restores parity with the narrow
// web screen and native's Agents screen, both of which already carry a Files entry. `/files`
// is the same cross-workspace view all three open.
//
// The genome and the pile share ONE selection — pressing an agent on the figure brings
// its card to the front of the stack, and pressing a card behind the front one selects
// it on the figure. The Maestro holds that selection at rest, and pressing the current
// selection again returns to it. That behaviour, and the reads behind it, live in
// `useYourAgents` so the narrow screen is the same product with a different layout.

export function YourAgentsDesktop() {
  const { t } = useTranslation();
  const { team, loading, agents, skills, maestro, selectedId, select, openSkills, addAgent } = useYourAgents();
  // Hold the whole row back until BOTH reads the tiles need have settled — see
  // `useSummaryTilesReady`: two of the four tiles render nothing until theirs lands, so without this
  // the row assembles in front of the reader (three tiles, then a fourth shoving them along).
  const tilesReady = useSummaryTilesReady();

  if (team.status === 'error') {
    return (
      <Typography variant="p1" color="secondary" className={styles.error}>
        {t('agents.error')}
      </Typography>
    );
  }

  // Nothing until both reads land: see `loading` in useYourAgents for why the whole screen
  // waits rather than filling in around a half-known team.
  if (loading) return <PageLoader />;

  // No team yet → the intro replaces the whole screen (no genome, no pile, no
  // destinations), matching native's empty branch. It bottom-anchors so the CTA sits a
  // fixed 198px from the screen edge, as the frame specs.
  if (agents.length === 0)
    return (
      <div className={styles.emptyWrap}>
        <AgentsEmptyIntro />
      </div>
    );

  return (
    <div className={styles.body}>
      {/* Four tiles above the team radar, in the design's order — Tasks · Routines · Files · Apps
          (Figma `15378:117541`), which is also the order both phones and this screen's own narrow
          layout use. It read Routines-first here alone. The SAME four a Space's Dashboard tab heads
          with, so the two surfaces answer the same question the same way. The whole row waits for the tasks AND
          routines queries to settle, so it appears as one complete line rather than filling itself in
          tile by tile. Once settled, an account with no tasks keeps the tasks tile in its "No tasks yet" empty
          state beside the others. The full tabbed task list lives on `/tasks`. */}
      {tilesReady && (
        <div className={styles.topRow}>
          <TasksSummary />
          <RoutinesSummary />
          <FilesSummaryCard />
          <AppsSummaryCard />
        </div>
      )}

      <div className={styles.team}>
        <TeamGenomeCard
          agents={agents}
          skillNames={skills.map((skill) => skill.name)}
          skillCount={skills.length}
          maestroName={maestro.name}
          maestroId={MAESTRO_PILE_ID}
          selectedId={selectedId}
          onSelect={select}
          onOpenSkills={openSkills}
          onAddAgent={addAgent}
        />
        <AgentPile
          agents={agents}
          maestroName={maestro.name}
          maestroShortName={maestro.short}
          maestroRole={t('agents.maestro.conductor')}
          maestroDescription={t('agents.subtitle', { maestroAgentName: maestro.name })}
          selectedId={selectedId}
          onSelect={select}
        />
      </div>

      <div className={styles.destinations}>
        <DestinationCard
          title={t('agents.destinations.knowledge.title')}
          subtitle={t('agents.destinations.knowledge.subtitle')}
          icon={<KnowledgeIcon />}
          href="/knowledge"
          button="knowledge"
        />
      </div>
    </div>
  );
}
